import { useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import cx from "classnames";
import Input from "../Input";
import FormErrorMessage from "components/FormErrorMessage";
import t from "utils/translate";
import styles from "./styles.module.scss";

export interface SingleBirthdateInput {
  date: string;
  onDateComplete: (date: Date) => void;
  onDateError: () => void;
}

export const SingleBirthdateInput: React.FC<SingleBirthdateInput> = ({
  date,
  onDateComplete,
  onDateError
}) => {
  const [year, month, day] = date?.split("-") || [];
  const initialDate = month && day && year ? `${month}-${day}-${year}` : "";
  const [dateValue, setDateValue] = useState(initialDate || "");
  const [hasError, setError] = useState(false);
  const [ageError, setAgeError] = useState(false);
  const memoizedOnDateComplete = useCallback(onDateComplete, []);

  const isValidRealDate = (dateString: string): boolean => {
    // Check if the date string is complete (MM-DD-YYYY format)
    if (!/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      return false;
    }

    const [monthStr, dayStr, yearStr] = dateString.split("-");
    const month = parseInt(monthStr, 10);
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);

    // Basic range checks
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
      return false;
    }

    // Create date and check if it matches the input
    const dateObj = new Date(year, month - 1, day);
    
    // Check if the date object represents the same date as input
    return (
      dateObj.getFullYear() === year &&
      dateObj.getMonth() === month - 1 &&
      dateObj.getDate() === day
    );
  };

  useEffect(() => {
    // Only proceed if we have a complete date string
    if (!dateValue || dateValue.length < 10) {
      return;
    }

    const isRealDate = isValidRealDate(dateValue);
    
    if (!isRealDate) {
      setAgeError(false);
      setError(true);
      onDateError();
      return;
    }

    // Replace dashes with slashes to support Date object in Safari.
    const dateValueSlashes = dateValue.replace(/-/g, "/");
    const selectedDate = new Date(dateValueSlashes);
    const today = new Date();
    let age = today.getFullYear() - selectedDate.getFullYear();
    const monthDifference = today.getMonth() - selectedDate.getMonth();
    const dayDifference = today.getDate() - selectedDate.getDate();

    // Adjust age if the birthdate hasn't occurred yet this year
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    if (age >= 18 && age <= 200) {
      setError(false);
      setAgeError(false);
      memoizedOnDateComplete(selectedDate);
    } else {
      setError(false);
      setAgeError(true);
      onDateError();
    }
  }, [dateValue, memoizedOnDateComplete, onDateError]);

  const onDateChange = e => {
    setDateValue(e.target.value);
  };

  return (
    <section
      className={cx({
        [styles.singleBirthdateHasValue]: dateValue
      })}
    >
      <label htmlFor="birthdate" className={styles.hidden}>
        {"MM-DD-YYYY"}
      </label>
      <InputMask mask="99-99-9999" value={dateValue} onChange={onDateChange}>
        {inputProps => (
          <Input
            id="birthdate"
            data-testid="birthdate"
            {...inputProps}
            aria-required="true"
            placeholder="MM-DD-YYYY"
            error={hasError || ageError}
          />
        )}
      </InputMask>
      {(hasError || ageError) && (
        <FormErrorMessage role="alert" data-test="height-error">
          {ageError ? t("DOB_INVALID_AGE") : t("DOB_INVALID")}
        </FormErrorMessage>
      )}
    </section>
  );
};
